import React, { useCallback, useRef, useState } from 'react';

import ConfirmDialog from '../components/ConfirmDialog';

export interface DiscardGuardOptions {
  /** The form differs from what the dialog opened with (server values, or the empty defaults). */
  isDirty: boolean;
  /** A save is in flight: every close path and the guarded actions do nothing. */
  isBusy?: boolean;
  /** Closes the host dialog. */
  onClose: () => void;
  /** Puts the form back to what it opened with; runs before onClose or the guarded action. */
  onDiscard?: () => void;
  /**
   * Names the record in the confirmation, with glossary nouns:
   * `role "Viewer"`, `event type "Car"`, `the new sensor`.
   */
  recordNoun: string;
}

export interface DiscardGuard {
  /**
   * Pass straight to `<Dialog onOpenChange>`. Radix sends the X, Escape and an
   * outside click through it; opening is ignored.
   */
  onOpenChange: (open: boolean) => void;
  /** For a Cancel button. */
  requestClose: () => void;
  /**
   * Runs `action` at once when clean, or after "Discard changes" when dirty.
   * For a destructive action raised from a dirty form (Delete, Decommission).
   */
  confirmDiscard: (action: () => void) => void;
  /** Render as a sibling of the host `<Dialog>`. */
  guardDialog: React.ReactElement;
}

/**
 * The one guard against losing typed input (DESIGN_SYSTEM.md §5 Dialogs —
 * anatomy). Every close path of an input dialog routes here: a clean form
 * closes, a dirty one asks "Discard unsaved changes?" with "Keep editing" /
 * "Discard changes". The confirm is variant `default`, not destructive: it
 * throws away input, not stored data. While a save is pending the guard is
 * inert (the kit `<Dialog pending>` already swallows closing).
 */
export function useDiscardGuard({
  isDirty,
  isBusy = false,
  onClose,
  onDiscard,
  recordNoun,
}: DiscardGuardOptions): DiscardGuard {
  const [open, setOpen] = useState(false);
  // A ref, not state: setState(fn) would CALL the action as an updater.
  const pendingAction = useRef<(() => void) | null>(null);

  const confirmDiscard = useCallback(
    (action: () => void) => {
      if (isBusy) return;
      if (!isDirty) {
        action();
        return;
      }
      pendingAction.current = action;
      setOpen(true);
    },
    [isBusy, isDirty],
  );

  const requestClose = useCallback(() => confirmDiscard(onClose), [confirmDiscard, onClose]);

  const onOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) return;
      requestClose();
    },
    [requestClose],
  );

  const guardDialog = (
    <ConfirmDialog
      open={open}
      title="Discard unsaved changes?"
      description={`Your changes to ${recordNoun} haven't been saved.`}
      cancelLabel="Keep editing"
      confirmLabel="Discard changes"
      variant="default"
      errorContext="Couldn't discard the changes"
      onConfirm={async () => {
        const action = pendingAction.current;
        pendingAction.current = null;
        onDiscard?.();
        action?.();
      }}
      onClose={() => {
        pendingAction.current = null;
        setOpen(false);
      }}
    />
  );

  return { onOpenChange, requestClose, confirmDiscard, guardDialog };
}
